import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { ContestsManager } from '@/components/Contests';
import { WeeklyPlanTable } from '@/components/WeeklyPlanTable';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, LogOut, ShieldCheck, GraduationCap, User, BookOpen,
  CalendarDays, ClipboardList, StickyNote, Video, BarChart3,
  CheckCircle, XCircle, Target, Trophy, Clock, Eye,
  ChevronDown, ChevronUp, FileSpreadsheet, Activity, Search,
  TrendingUp, Layers, MessageSquare, Star, Award, AlertTriangle,
  Download, Paperclip, File as FileIcon, Send, Loader2, Mail, MailOpen,
  Pencil, Trash2, Camera, Edit3, Home, MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Avatar with real DB picture (get_avatar is public → direct URL)
function UserAvatar({ user, className = '', fallback = null }) {
  const src = user?.avatar_path ? `${API_URL}/api/profile/avatar/${user.avatar_path}` : null;
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {src ? <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" /> : (fallback ?? (user?.name?.charAt(0) || '؟'))}
    </div>
  );
}

const MAIN_TABS = [
  { id: 'stats', label: 'الإحصائيات', icon: BarChart3 },
  { id: 'teachers', label: 'المعلمون', icon: GraduationCap },
  { id: 'students', label: 'الطلاب', icon: Users },
  { id: 'messages', label: 'رسائل المدير', icon: Mail },
  { id: 'minister-msgs', label: 'رسائل الوزير', icon: Star },
  { id: 'contests', label: 'المسابقات', icon: Award },
];

export default function PrincipalPage() {
  const { user, logout, getAuthHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [overview, setOverview] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', bio: '', email: '', phone: '' });
  const [displayName, setDisplayName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'principal') navigate('/grades');
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === 'principal') {
      const h = getAuthHeaders();
      axios.get(`${API_URL}/api/principal/overview`, { headers: h })
        .then(r => setOverview(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/auth/me`, { headers: h }).then(async r => {
        setProfileForm({ name: r.data.name || '', bio: r.data.bio || '', email: r.data.email || '', phone: r.data.phone || '' });
        setDisplayName(r.data.name || '');
        if (r.data.avatar_path) {
          try { const av = await axios.get(`${API_URL}/api/profile/avatar/${r.data.avatar_path}`, { headers: h, responseType: 'blob' }); setAvatarUrl(URL.createObjectURL(av.data)); } catch {}
        }
      }).catch(() => setDisplayName(user.name || ''));
    }
  }, [user, getAuthHeaders]);

  const saveProfile = async () => {
    if (!profileForm.name.trim()) { toast.error('الاسم مطلوب'); return; }
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/profile/update`, profileForm, { headers: getAuthHeaders() });
      setDisplayName(profileForm.name.trim());
      toast.success('تم حفظ الملف الشخصي'); setShowProfile(false);
    } catch { toast.error('فشل الحفظ'); } finally { setSavingProfile(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API_URL}/api/profile/avatar`, fd, { headers: getAuthHeaders() });
      setAvatarUrl(URL.createObjectURL(file)); toast.success('تم تحديث الصورة');
    } catch { toast.error('فشل رفع الصورة'); }
  };

  if (!user || user.role !== 'principal') return null;

  const principalName = displayName || user.name || '';
  const gradeNames = (overview?.grade_numbers || []).map(g => `الصف ${g}`).join('، ');

  return (
    <div className="tpage" dir="rtl">
      <header className="nav">
        <h1>
          <span className="t"><ShieldCheck /></span>
          <span>لوحة مدير المدرسة{overview?.school && <small style={{ display: 'block', color: 'var(--mut)', fontSize: 12.5, fontWeight: 600 }}>{overview.school.name}</small>}</span>
        </h1>
        <div className="us">
          <span>{principalName}</span>
          <span className="ua" aria-hidden="true">{avatarUrl ? <img src={avatarUrl} alt="" /> : (principalName.charAt(0) || 'م')}</span>
          <button className="lo" onClick={() => { logout(); navigate('/login'); }} aria-label="تسجيل الخروج" data-testid="principal-logout"><LogOut /></button>
        </div>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        {/* Principal profile card */}
        <section className="cd tp" data-testid="principal-profile-card">
          <div className="tp-b" aria-hidden="true"><i></i><i></i><i></i></div>
          <div className="tp-c">
            <div className="tp-t">
              <div className="tp-av">
                {avatarUrl ? <img src={avatarUrl} alt="" /> : (principalName.charAt(0) || 'م')}
                <label className="tp-cam" title="تغيير الصورة"><input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} /><Camera /></label>
              </div>
              <div className="tp-n">
                <h2>{principalName}</h2>
                <div className="tp-ch">
                  <span className="chp" style={{ '--c': 'var(--pk)' }}><ShieldCheck />مدير المدرسة</span>
                  {overview?.school && <span className="chp">{overview.school.name}</span>}
                </div>
              </div>
              <button className="tp-ed" onClick={() => setShowProfile(true)} data-testid="edit-profile-btn"><Edit3 />تعديل الملف</button>
            </div>
            {profileForm.bio && <p className="tp-bio">{profileForm.bio}</p>}
            <div className="tp-d">
              <div className="dt"><div className="t"><Home /></div><div><span>المدرسة</span><b style={{ direction: 'rtl', textAlign: 'right' }}>{overview?.school?.name || '—'}</b></div></div>
              <div className="dt"><div className="t"><MapPin /></div><div><span>المديرية</span><b style={{ direction: 'rtl', textAlign: 'right' }}>{overview?.directorate_name || '—'}</b></div></div>
              <div className="dt"><div className="t"><Users /></div><div><span>الصفوف</span><b style={{ direction: 'rtl', textAlign: 'right' }}>{gradeNames || '—'}</b></div></div>
            </div>
            <div className="tp-s">
              <div className="tk" style={{ '--c': 'var(--med)' }}><b>{overview?.messages_sent ?? 0}</b><span>الرسائل المرسلة</span></div>
              <div className="tk" style={{ '--c': 'var(--hard)' }}><b>{overview?.contests_count ?? 0}</b><span>المسابقات</span></div>
              <div className="tk" style={{ '--c': 'var(--easy)' }}><b>{overview?.grade_count ?? 0}</b><span>الصفوف</span></div>
              <div className="tk" style={{ '--c': 'var(--pk)' }}><b>{overview?.student_count ?? 0}</b><span>الطلاب</span></div>
              <div className="tk" style={{ '--c': 'var(--pu)' }}><b>{overview?.teacher_count ?? 0}</b><span>المعلمون</span></div>
            </div>
          </div>
        </section>

        <div className="tabs" role="tablist" aria-label="أقسام لوحة المدير">
          {MAIN_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}
                className="tb" title={tab.label} data-testid={`principal-tab-${tab.id}`}>
                <Icon /><span className="tl">{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="pn" role="tabpanel">
          {activeTab === 'stats' && <PrincipalStatsView headers={getAuthHeaders()} />}
          {activeTab === 'teachers' && <TeachersView headers={getAuthHeaders()} />}
          {activeTab === 'students' && <StudentsView headers={getAuthHeaders()} />}
          {activeTab === 'messages' && <DirectorMessagesView headers={getAuthHeaders()} />}
          {activeTab === 'minister-msgs' && <MinisterMsgsView headers={getAuthHeaders()} />}
          {activeTab === 'contests' && <ContestsManager headers={getAuthHeaders()} token={token} scoped={true} lightDialogs={true} />}
        </div>
      </main>

      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="tdlg max-w-lg bg-[#1a1a2e] border-white/10 text-white" dir="rtl" data-testid="edit-profile-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><User className="w-5 h-5 text-[#4da6ff]" /> تعديل الملف الشخصي</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div><label className="text-white/60 text-xs font-bold block mb-1">الاسم</label>
              <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" data-testid="profile-name-input" /></div>
            <div><label className="text-white/60 text-xs font-bold block mb-1">نبذة تعريفية</label>
              <textarea value={profileForm.bio} onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))} rows={3} placeholder="اكتب نبذة قصيرة" className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none resize-none" data-testid="profile-bio-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-white/60 text-xs font-bold block mb-1">البريد الإلكتروني</label>
                <input value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} dir="ltr" className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none text-right" data-testid="profile-email-input" /></div>
              <div><label className="text-white/60 text-xs font-bold block mb-1">رقم الهاتف</label>
                <input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} dir="ltr" className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none text-right" data-testid="profile-phone-input" /></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveProfile} disabled={savingProfile} className="flex-1 bg-emerald-500 hover:bg-emerald-600" data-testid="save-profile-btn">{savingProfile ? '...' : 'حفظ'}</Button>
              <Button variant="ghost" onClick={() => setShowProfile(false)} className="text-white/50">إلغاء</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────
// TEACHERS VIEW
// ─────────────────────────────────────────────

function col(p) { return p < 50 ? 'var(--hard)' : p < 75 ? 'var(--med)' : 'var(--easy)'; }
function PRing({ pct, size, stroke, color, children }) {
  const r = (size - stroke) / 2, C = 2 * Math.PI * r, off = C * (1 - Math.min(pct || 0, 100) / 100), h = size / 2;
  return (
    <div className="rg" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`}><circle className="tk2" cx={h} cy={h} r={r} strokeWidth={stroke} /><circle className="fl" cx={h} cy={h} r={r} strokeWidth={stroke} transform={`rotate(-90 ${h} ${h})`} style={{ stroke: color, strokeDasharray: C, strokeDashoffset: off }} /></svg>
      <div className="in">{children}</div>
    </div>
  );
}
function PBar({ value, color, testId }) { return <span className="pb" data-testid={testId} style={{ '--c': color }}><i style={{ width: `${Math.max(value || 0, 2)}%` }} /></span>; }

// ─── Stats View ───
function PrincipalStatsView({ headers }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/principal/stats`, { headers }).then(r => setStats(r.data)).catch(() => {}); }, [headers]);
  if (!stats) return <LoadingSpinner />;
  const t = stats.totals;

  return (
    <div className="pn" data-testid="principal-stats">
      {/* Totals */}
      <div className="sg6">
        {[
          { v: t.teachers, l: 'معلم', c: 'var(--pu)', i: GraduationCap },
          { v: t.students, l: 'طالب', c: 'var(--pk)', i: Users },
          { v: t.plans, l: 'خطة', c: 'var(--easy)', i: CalendarDays },
          { v: t.assignments, l: 'واجب', c: 'var(--pu)', i: ClipboardList },
          { v: t.exams, l: 'امتحان', c: 'var(--hard)', i: FileSpreadsheet },
          { v: t.notes, l: 'ملاحظة', c: 'var(--med)', i: StickyNote },
        ].map(s => {
          const Icon = s.i;
          return (
            <div key={s.l} className="k6" style={{ '--c': s.c }}>
              <div className="t"><Icon /></div>
              <div><b>{s.v}</b><span>{s.l}</span></div>
            </div>
          );
        })}
      </div>

      <div className="g2 principal-overview-grid" data-testid="principal-performance-grid">
        {/* Subject Performance */}
        {stats.subject_performance.length > 0 && (
          <section className="cd pc principal-overview-card principal-performance-card" style={{ '--c': 'var(--pu)' }} data-testid="principal-subject-performance-card">
            <div className="ph" data-testid="principal-subject-performance-header"><div className="t" data-testid="principal-subject-performance-icon"><Activity /></div><h2 data-testid="principal-subject-performance-title">أداء المواد الدراسية</h2></div>
            <div className="sl">
              {stats.subject_performance.map((s, i) => {
                const c = col(s.avg_pct);
                return (
                  <div className="sr" key={i} style={{ '--c': c }} data-testid={`principal-subject-row-${i}`}>
                    <PRing pct={s.avg_pct} size={66} stroke={7} color={c}><b data-testid={`principal-subject-average-${i}`}>{s.avg_pct}%</b></PRing>
                    <div className="srx" data-testid={`principal-subject-content-${i}`}>
                      <h3 data-testid={`principal-subject-name-${i}`}>{s.name}</h3>
                      <div className="mt" data-testid={`principal-subject-metadata-${i}`}>
                        <span className="mx" style={{ '--c': 'var(--easy)' }} data-testid={`principal-subject-pass-rate-${i}`}>نسبة النجاح: {s.pass_rate}%</span>
                        <span className="mx" style={{ '--c': 'var(--pu)' }} data-testid={`principal-subject-mark-count-${i}`}><b>{s.count}</b> علامة</span>
                        {s.teachers?.length > 0 && <span data-testid={`principal-subject-teachers-${i}`}>المعلمون: {s.teachers.join('، ')}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {/* Per Grade */}
        {stats.per_grade.length > 0 && (
          <section className="cd pc principal-overview-card principal-performance-card" style={{ '--c': 'var(--pk)' }} data-testid="principal-grade-performance-card">
            <div className="ph" data-testid="principal-grade-performance-header"><div className="t" data-testid="principal-grade-performance-icon"><Activity /></div><h2 data-testid="principal-grade-performance-title">الأداء حسب الصف</h2></div>
            <div className="sl">
              {stats.per_grade.map(g => {
                const c = col(g.avg);
                return (
                  <div className="cr" key={g.grade} style={{ '--c': c }} data-testid={`principal-grade-row-${g.grade}`}>
                    <div className="cr-h"><b data-testid={`principal-grade-name-${g.grade}`}>الصف {g.grade}</b><span data-testid={`principal-grade-details-${g.grade}`}>{g.students} طلاب · {g.lessons} دروس</span><em data-testid={`principal-grade-average-${g.grade}`}>{g.avg}%</em></div>
                    <PBar value={g.avg} color={c} testId={`principal-grade-progress-${g.grade}`} />
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <div className="g2 principal-overview-grid" data-testid="principal-teacher-overview-grid">
        {/* Teacher Rankings */}
        <section className="cd pc principal-overview-card" style={{ '--c': 'var(--easy)' }} data-testid="principal-teacher-ranking-card">
          <div className="ph" data-testid="principal-teacher-ranking-header"><div className="t" data-testid="principal-teacher-ranking-icon"><GraduationCap /></div><h2 data-testid="principal-teacher-ranking-title">ترتيب المعلمين حسب النشاط</h2></div>
          <div className="sl">
            {stats.teachers.map((tc, i) => {
              const mx = Math.max(...stats.teachers.map(x => x.total_activity)) || 1;
              return (
                <div className="rk" key={tc.id} style={{ '--c': i === 0 ? 'var(--pk)' : 'var(--mut)' }} data-testid={`principal-teacher-ranking-row-${tc.id}`}>
                  <span className="no" data-testid={`principal-teacher-rank-${tc.id}`}>{i + 1}</span>
                  <UserAvatar user={tc} className="av" />
                  <div className="rk-b">
                    <div className="rk-h"><b data-testid={`principal-ranked-teacher-name-${tc.id}`}>{tc.name}</b><small data-testid={`principal-ranked-teacher-students-${tc.id}`}>{tc.student_count} طالب</small></div>
                    <PBar value={Math.round(tc.total_activity / mx * 100)} color={i === 0 ? 'var(--pk)' : 'var(--pu)'} testId={`principal-teacher-activity-progress-${tc.id}`} />
                    <div className="mt">
                      <span className="mx" style={{ '--c': 'var(--med)' }} data-testid={`principal-ranked-teacher-activity-${tc.id}`}><b>{tc.total_activity}</b> نشاط</span>
                      <span className="mx" style={{ '--c': 'var(--hard)' }} data-testid={`principal-ranked-teacher-exams-${tc.id}`}><b>{tc.exams}</b> امتحان</span>
                      <span className={`mx${tc.assignments ? '' : ' z'}`} style={{ '--c': 'var(--pu)' }} data-testid={`principal-ranked-teacher-assignments-${tc.id}`}><b>{tc.assignments}</b> واجب</span>
                      <span className={`mx${tc.plans ? '' : ' z'}`} style={{ '--c': 'var(--easy)' }} data-testid={`principal-ranked-teacher-plans-${tc.id}`}><b>{tc.plans}</b> خطة</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        {/* Weak Teachers */}
        <section className="cd pc principal-overview-card" style={{ '--c': 'var(--med)' }} data-testid="principal-teacher-followup-card">
          <div className="ph" data-testid="principal-teacher-followup-header"><div className="t" data-testid="principal-teacher-followup-icon"><AlertTriangle /></div><h2 data-testid="principal-teacher-followup-title">معلمون يحتاجون متابعة</h2></div>
          {stats.weak_teachers.length > 0 ? (
            <div className="sl">
              {stats.weak_teachers.map(tc => (
                <div className="fw" key={tc.id} data-testid={`principal-teacher-followup-row-${tc.id}`}>
                  <div className="avw"><UserAvatar user={tc} className="av" /><span className="wb"><AlertTriangle /></span></div>
                  <div className="rk-b">
                    <div className="rk-h"><b data-testid={`principal-followup-teacher-name-${tc.id}`}>{tc.name}</b><small data-testid={`principal-followup-teacher-students-${tc.id}`}>{tc.student_count} طالب</small></div>
                    <div className="mt">
                      <span className="mx" style={{ '--c': 'var(--med)' }} data-testid={`principal-followup-teacher-activity-${tc.id}`}><b>{tc.total_activity}</b> نشاط</span>
                      <span className={`mx${tc.exams ? '' : ' z'}`} style={{ '--c': 'var(--hard)' }} data-testid={`principal-followup-teacher-exams-${tc.id}`}><b>{tc.exams}</b> امتحان</span>
                      <span className={`mx${tc.assignments ? '' : ' z'}`} style={{ '--c': 'var(--pu)' }} data-testid={`principal-followup-teacher-assignments-${tc.id}`}><b>{tc.assignments}</b> واجب</span>
                      <span className={`mx${tc.plans ? '' : ' z'}`} style={{ '--c': 'var(--easy)' }} data-testid={`principal-followup-teacher-plans-${tc.id}`}><b>{tc.plans}</b> خطة</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="em" style={{ '--c': 'var(--easy)' }} data-testid="principal-teacher-followup-empty"><div className="t"><CheckCircle /></div><b>كل المعلمين نشطون</b></div>}
        </section>
      </div>

      <div className="g2 principal-overview-grid" data-testid="principal-student-overview-grid">
        {/* Top Students */}
        <section className="cd pc principal-overview-card" style={{ '--c': 'var(--easy)' }} data-testid="principal-top-students-card">
          <div className="ph" data-testid="principal-top-students-header"><div className="t" data-testid="principal-top-students-icon"><TrendingUp /></div><h2 data-testid="principal-top-students-title">أفضل الطلاب</h2></div>
          {stats.top_students.filter(s => s.avg_grade > 0).length > 0 ? (
            <div className="sl">
              {stats.top_students.filter(s => s.avg_grade > 0).map((s, i) => {
                const c = col(s.avg_grade);
                return (
                  <div className="ts" key={s.id} style={{ '--c': c }} data-testid={`principal-top-student-row-${s.id}`}>
                    <span className="no" data-testid={`principal-top-student-rank-${s.id}`}>{i + 1}</span>
                    <UserAvatar user={s} className="av" />
                    <div className="rk-b"><div className="rk-h"><b data-testid={`principal-top-student-name-${s.id}`}>{s.name}</b>{s.grade_number && <small data-testid={`principal-top-student-grade-${s.id}`}>الصف {s.grade_number}</small>}<em data-testid={`principal-top-student-average-${s.id}`}>{s.avg_grade}%</em></div><PBar value={s.avg_grade} color={c} testId={`principal-top-student-progress-${s.id}`} /></div>
                  </div>
                );
              })}
            </div>
          ) : <div className="em" data-testid="principal-top-students-empty"><div className="t"><TrendingUp /></div><b>لا توجد بيانات</b></div>}
        </section>
        {/* Weak Students */}
        <section className="cd pc principal-overview-card" style={{ '--c': 'var(--hard)' }} data-testid="principal-student-followup-card">
          <div className="ph" data-testid="principal-student-followup-header"><div className="t" data-testid="principal-student-followup-icon"><AlertTriangle /></div><h2 data-testid="principal-student-followup-title">طلاب يحتاجون متابعة</h2></div>
          {stats.weak_students.filter(s => s.avg_grade > 0 && s.avg_grade < 60).length > 0 ? (
            <div className="sl">
              {stats.weak_students.filter(s => s.avg_grade > 0 && s.avg_grade < 60).map(s => (
                <div className="ts" key={s.id} style={{ '--c': 'var(--hard)' }} data-testid={`principal-followup-student-row-${s.id}`}>
                  <span className="no" data-testid={`principal-followup-student-warning-${s.id}`}>!</span>
                  <UserAvatar user={s} className="av" />
                  <div className="rk-b"><div className="rk-h"><b data-testid={`principal-followup-student-name-${s.id}`}>{s.name}</b>{s.grade_number && <small data-testid={`principal-followup-student-grade-${s.id}`}>الصف {s.grade_number}</small>}<em data-testid={`principal-followup-student-average-${s.id}`}>{s.avg_grade}%</em></div><PBar value={s.avg_grade} color="var(--hard)" testId={`principal-followup-student-progress-${s.id}`} /></div>
                </div>
              ))}
            </div>
          ) : <div className="em" style={{ '--c': 'var(--easy)' }} data-testid="principal-student-followup-empty"><div className="t"><CheckCircle /></div><b>لا يوجد طلاب ضعاف</b><span>كل الطلاب بمستوى جيد</span></div>}
        </section>
      </div>
    </div>
  );
}


function TeachersView({ headers }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/principal/teachers`, { headers })
      .then(r => setTeachers(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="pn" data-testid="principal-teachers">
      <div className="hd" style={{ '--c': 'var(--pu)' }}>
        <div className="t"><GraduationCap /></div>
        <h2>المعلمون ({teachers.length})</h2>
      </div>
      <div className="tgrid">
        {teachers.map(t => {
          const weak = t.total_activity < 20 || !t.assignments || !t.plans;
          return (
            <section key={t.id} className="cd tc" onClick={() => setSelectedTeacher(t)} data-testid={`teacher-card-${t.id}`} style={{ cursor: 'pointer' }}>
              <div className="tc-h">
                <UserAvatar user={t} className="av" fallback={t.name?.charAt(0) || 'م'} />
                <div><h3>{t.name}</h3><p>{t.student_count} طالب</p></div>
                <span className="chp" style={{ '--c': weak ? 'var(--med)' : 'var(--easy)' }}>{weak ? 'يحتاج متابعة' : 'نشط'}</span>
              </div>
              <div className="tm">
                <div style={{ '--c': 'var(--med)' }}><b className={t.total_activity ? '' : 'z'}>{t.total_activity}</b><span>نشاط</span></div>
                <div style={{ '--c': 'var(--hard)' }}><b className={t.exams ? '' : 'z'}>{t.exams}</b><span>امتحان</span></div>
                <div style={{ '--c': 'var(--pu)' }}><b className={t.assignments ? '' : 'z'}>{t.assignments}</b><span>واجب</span></div>
                <div style={{ '--c': 'var(--easy)' }}><b className={t.plans ? '' : 'z'}>{t.plans}</b><span>خطة</span></div>
              </div>
              <button className="bt sc sm" onClick={e => { e.stopPropagation(); setSelectedTeacher(t); }} data-testid={`teacher-open-${t.id}`}><Eye />عرض التفاصيل</button>
            </section>
          );
        })}
      </div>
      {teachers.length === 0 && <div className="em"><div className="t"><GraduationCap /></div><b>لا يوجد معلمون في المدرسة</b></div>}
      {selectedTeacher && <TeacherDetailDialog teacher={selectedTeacher} headers={headers} onClose={() => setSelectedTeacher(null)} />}
    </div>
  );
}


// ─────────────────────────────────────────────
// TEACHER DETAIL DIALOG (Comprehensive)
// ─────────────────────────────────────────────
const TEACHER_TABS = [
  { id: 'overview', label: 'نظرة عامة', icon: Activity },
  { id: 'weekly-plans', label: 'الخطط الأسبوعية', icon: CalendarDays },
  { id: 'grades-sheet', label: 'كشف العلامات', icon: FileSpreadsheet },
  { id: 'assignments', label: 'الواجبات', icon: ClipboardList },
  { id: 'notes', label: 'الملاحظات', icon: StickyNote },
  { id: 'my-notes', label: 'رسائل المدير', icon: MessageSquare },
];

function TeacherDetailDialog({ teacher, headers, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [activityData, setActivityData] = useState(null);
  const [weeklyPlans, setWeeklyPlans] = useState(null);
  const [weeklyPlansError, setWeeklyPlansError] = useState(false);
  const [gradesSheet, setGradesSheet] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [notes, setNotes] = useState(null);
  const [principalComments, setPrincipalComments] = useState(null);

  // Load overview on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/principal/teachers/${teacher.id}/activity`, { headers })
      .then(r => setActivityData(r.data)).catch(() => {});
  }, [teacher.id, headers]);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === 'weekly-plans' && !weeklyPlans && !weeklyPlansError) {
      axios.get(`${API_URL}/api/principal/teachers/${teacher.id}/weekly-plans`, { headers })
        .then(r => setWeeklyPlans(r.data)).catch(() => { setWeeklyPlansError(true); toast.error('فشل تحميل الخطط'); });
    }
    if (activeTab === 'grades-sheet' && !gradesSheet) {
      axios.get(`${API_URL}/api/principal/teachers/${teacher.id}/grades-sheet`, { headers })
        .then(r => setGradesSheet(r.data)).catch(() => toast.error('فشل تحميل كشف العلامات'));
    }
    if (activeTab === 'assignments' && !assignments) {
      axios.get(`${API_URL}/api/principal/teachers/${teacher.id}/assignments`, { headers })
        .then(r => setAssignments(r.data)).catch(() => toast.error('فشل تحميل الواجبات'));
    }
    if (activeTab === 'notes' && !notes) {
      axios.get(`${API_URL}/api/principal/teachers/${teacher.id}/notes`, { headers })
        .then(r => setNotes(r.data)).catch(() => toast.error('فشل تحميل الملاحظات'));
    }
    if (activeTab === 'my-notes' && !principalComments) {
      axios.get(`${API_URL}/api/principal/teacher-comments/${teacher.id}`, { headers })
        .then(r => setPrincipalComments(r.data)).catch(() => toast.error('فشل التحميل'));
    }
  }, [activeTab, teacher.id, headers, weeklyPlans, weeklyPlansError, gradesSheet, assignments, notes, principalComments]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="tdlg max-w-4xl bg-[#1a1a2e] border-white/10 text-white max-h-[90vh] overflow-hidden p-0" dir="rtl" data-testid="principal-teacher-detail-dialog">
        <DialogDescription className="sr-only">تفاصيل المعلم</DialogDescription>
        {/* Header */}
        <div className="bg-gradient-to-l from-indigo-500/10 to-[#1a1a2e] p-5 border-b border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <UserAvatar user={teacher} className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-lg shrink-0" fallback={<GraduationCap className="w-6 h-6 text-white" />} />
              <div>
                <span className="text-xl font-bold">{teacher.name}</span>
                <p className="text-white/40 text-xs">@{teacher.username} - {teacher.student_count} طالب</p>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Sub-Tabs */}
        <div className="flex gap-1 px-4 pt-3 overflow-x-auto no-scrollbar">
          {TEACHER_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                }`} data-testid={`teacher-detail-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && <TeacherOverviewTab data={activityData} />}
          {activeTab === 'weekly-plans' && (weeklyPlansError ? <div role="alert" data-testid="principal-weekly-plans-error" className="text-center space-y-3"><p>تعذر تحميل الخطط الأسبوعية</p><Button variant="outline" data-testid="principal-weekly-plans-retry" onClick={() => setWeeklyPlansError(false)}>إعادة المحاولة</Button></div> : <TeacherWeeklyPlansTab data={weeklyPlans} />)}
          {activeTab === 'grades-sheet' && <TeacherGradesSheetTab data={gradesSheet} />}
          {activeTab === 'assignments' && <TeacherAssignmentsTab data={assignments} />}
          {activeTab === 'notes' && <TeacherNotesTab data={notes} />}
          {activeTab === 'my-notes' && <PrincipalTeacherCommentsTab
            comments={principalComments} teacherId={teacher.id} headers={headers}
            onRefresh={() => { setPrincipalComments(null); axios.get(`${API_URL}/api/principal/teacher-comments/${teacher.id}`, { headers }).then(r => setPrincipalComments(r.data)); }}
          />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Teacher Overview Tab
function TeacherOverviewTab({ data }) {
  if (!data) return <LoadingSpinner />;

  const ACTIVITY_ICONS = {
    weekly_plan: { icon: CalendarDays, color: 'text-emerald-400 bg-emerald-500/10', label: 'خطة أسبوعية' },
    assignment: { icon: ClipboardList, color: 'text-[#4da6ff] bg-[#4da6ff]/10', label: 'واجب' },
    note: { icon: StickyNote, color: 'text-amber-400 bg-amber-500/10', label: 'ملاحظة' },
    video: { icon: Video, color: 'text-purple-400 bg-purple-500/10', label: 'فيديو' },
    exam: { icon: FileSpreadsheet, color: 'text-red-400 bg-red-500/10', label: 'امتحان' },
  };

  return (
    <div className="space-y-4" data-testid="teacher-overview-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: 'خطط', value: data.summary.plans, color: 'text-emerald-400', bg: 'bg-emerald-500/5' },
          { label: 'واجبات', value: data.summary.assignments, color: 'text-[#4da6ff]', bg: 'bg-[#4da6ff]/5' },
          { label: 'ملاحظات', value: data.summary.notes, color: 'text-amber-400', bg: 'bg-amber-500/5' },
          { label: 'فيديوهات', value: data.summary.videos, color: 'text-purple-400', bg: 'bg-purple-500/5' },
          { label: 'امتحانات', value: data.summary.exams, color: 'text-red-400', bg: 'bg-red-500/5' },
        ].map(s => (
          <div key={s.label} className={`text-center p-3 rounded-xl ${s.bg} border border-white/5`}>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[9px] text-white/40 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Activity Timeline */}
      <div>
        <h4 className="text-white/60 text-xs font-bold flex items-center gap-1 mb-3"><Activity className="w-3.5 h-3.5" /> آخر النشاطات</h4>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {data.activity.map((a, idx) => {
            const config = ACTIVITY_ICONS[a.type] || ACTIVITY_ICONS.note;
            const Icon = config.icon;
            return (
              <div key={idx} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 hover:bg-white/5 transition-colors">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-white/40 text-[9px] px-1.5 py-0.5 rounded bg-white/5">{config.label}</span>
                    {a.subject_name && <span className="text-emerald-400/60 text-[9px]">{a.subject_name}</span>}
                  </div>
                  <p className="text-white/80 text-xs mt-0.5">{a.title || a.note || a.lesson_title || ''}</p>
                  {a.student_name && <p className="text-white/30 text-[10px]">للطالب: {a.student_name}</p>}
                  {a.read_count !== undefined && <p className="text-white/30 text-[10px]">{a.read_count} قرأها</p>}
                  {a.grades_count !== undefined && <p className="text-white/30 text-[10px]">{a.grades_count} طالب - {a.max_mark} علامة</p>}
                </div>
                <span className="text-white/20 text-[9px] shrink-0">{formatDate(a.created_at)}</span>
              </div>
            );
          })}
          {data.activity.length === 0 && <EmptyState text="لا يوجد نشاط" small />}
        </div>
      </div>
    </div>
  );
}

// Teacher Weekly Plans Tab
function TeacherWeeklyPlansTab({ data }) {
  const [expandedPlan, setExpandedPlan] = useState(null);

  if (!data) return <LoadingSpinner />;
  const plans = Array.isArray(data.plans) ? data.plans.filter(plan => plan && typeof plan === 'object') : [];

  return (
    <div className="space-y-3" data-testid="teacher-weekly-plans-tab">
      <p className="text-white/40 text-xs" data-testid="principal-weekly-plans-count">{plans.length} خطة أسبوعية</p>
      {plans.map(plan => (
        <div key={plan.id} className="rounded-xl bg-white/[0.03] border border-white/5 overflow-hidden">
          <button className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
            onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
            aria-expanded={expandedPlan === plan.id}
            data-testid={`weekly-plan-${plan.id}`}>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-emerald-400" />
              <span className="text-white text-sm font-medium" data-testid={`principal-weekly-plan-title-${plan.id}`}>{plan.week_label || 'خطة أسبوعية'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/30 text-[10px]" data-testid={`principal-weekly-plan-date-${plan.id}`}>{formatDate(plan.created_at)}</span>
              {expandedPlan === plan.id ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
            </div>
          </button>
          {expandedPlan === plan.id && (
            <div className="border-t border-white/5 p-3 min-w-0">
              <WeeklyPlanTable plan={plan} testId={`principal-weekly-plan-table-${plan.id}`} />
            </div>
          )}
        </div>
      ))}
      {plans.length === 0 && <div data-testid="principal-weekly-plans-empty"><EmptyState text="لم يقدم المعلم أي خطة أسبوعية" /></div>}
    </div>
  );
}

// Teacher Grades Sheet Tab (كشف العلامات)
function TeacherGradesSheetTab({ data }) {
  const [selectedExam, setSelectedExam] = useState(null);

  if (!data) return <LoadingSpinner />;

  return (
    <div className="space-y-4" data-testid="teacher-grades-sheet-tab">
      <p className="text-white/40 text-xs">{data.exams.length} امتحان - {data.students.length} طالب</p>

      {/* Exam Cards */}
      {data.exams.map(exam => {
        const isExpanded = selectedExam === exam.id;
        const avgMark = exam.grades.length > 0
          ? Math.round(exam.grades.reduce((s, g) => s + (g.mark || 0), 0) / exam.grades.length)
          : 0;
        const passingStudents = exam.grades.filter(g => g.mark >= exam.max_mark * 0.5).length;

        return (
          <div key={exam.id} className="rounded-xl bg-white/[0.03] border border-white/5 overflow-hidden">
            <button className="w-full p-3 hover:bg-white/5 transition-colors"
              onClick={() => setSelectedExam(isExpanded ? null : exam.id)}
              data-testid={`exam-card-${exam.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-4 h-4 text-red-400" />
                  <div className="text-right">
                    <p className="text-white text-sm font-bold">{exam.title}</p>
                    <p className="text-white/30 text-[10px]">{exam.subject_name} - العلامة القصوى: {exam.max_mark}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-left">
                    <p className="text-xs text-white/40">{exam.grades.length} طالب</p>
                    {exam.grades.length > 0 && <p className="text-[10px] text-white/30">معدل: {avgMark}/{exam.max_mark}</p>}
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                </div>
              </div>
              {/* Quick Stats */}
              {exam.grades.length > 0 && (
                <div className="flex gap-3 mt-2 text-[10px]">
                  <span className="text-emerald-400">{passingStudents} ناجح</span>
                  <span className="text-red-400">{exam.grades.length - passingStudents} راسب</span>
                  <span className="text-[#4da6ff]">معدل: {Math.round((avgMark / exam.max_mark) * 100)}%</span>
                </div>
              )}
            </button>

            {/* Expanded: Full Grades Table */}
            {isExpanded && (
              <div className="border-t border-white/5 p-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-2 px-2 text-white/40 text-right font-medium">#</th>
                      <th className="py-2 px-2 text-white/40 text-right font-medium">الطالب</th>
                      <th className="py-2 px-2 text-white/40 text-center font-medium">العلامة</th>
                      <th className="py-2 px-2 text-white/40 text-center font-medium">النسبة</th>
                      <th className="py-2 px-2 text-white/40 text-center font-medium">الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.students.map((student, idx) => {
                      const grade = exam.grades.find(g => g.student_id === student.id);
                      const mark = grade?.mark;
                      const pct = mark !== undefined ? Math.round((mark / exam.max_mark) * 100) : null;
                      const passed = pct !== null && pct >= 50;
                      return (
                        <tr key={student.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-2 text-white/30">{idx + 1}</td>
                          <td className="py-2 px-2">
                            <span className="text-white/80">{student.name}</span>
                            <span className="text-white/20 text-[10px] mr-2">({student.username})</span>
                          </td>
                          <td className="py-2 px-2 text-center">
                            {mark !== undefined
                              ? <span className={`font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{mark}</span>
                              : <span className="text-white/10">-</span>
                            }
                          </td>
                          <td className="py-2 px-2 text-center">
                            {pct !== null
                              ? <span className={`${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</span>
                              : <span className="text-white/10">-</span>
                            }
                          </td>
                          <td className="py-2 px-2 text-center">
                            {mark !== undefined
                              ? passed
                                ? <span className="text-emerald-400 flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3" /> ناجح</span>
                                : <span className="text-red-400 flex items-center justify-center gap-1"><XCircle className="w-3 h-3" /> راسب</span>
                              : <span className="text-white/10">-</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {data.students.length === 0 && <EmptyState text="لا يوجد طلاب مسجلون لهذا المعلم" small />}
              </div>
            )}
          </div>
        );
      })}
      {data.exams.length === 0 && <EmptyState text="لم يُنشئ المعلم أي امتحان بعد" />}
    </div>
  );
}

// Teacher Assignments Tab
function TeacherAssignmentsTab({ data }) {
  if (!data) return <LoadingSpinner />;

  return (
    <div className="space-y-3" data-testid="teacher-assignments-tab">
      <p className="text-white/40 text-xs">{data.assignments.length} واجب</p>
      {data.assignments.map(a => (
        <div key={a.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-white text-sm font-bold">{a.title}</p>
              <p className="text-white/30 text-[10px]">{a.subject_name}{a.due_date ? ` - تسليم: ${a.due_date}` : ''}</p>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-emerald-400">
              <Eye className="w-3 h-3" /> {a.read_count} قرأها
            </div>
          </div>
          {a.description && <p className="text-white/50 text-xs leading-relaxed">{a.description}</p>}
          {/* Attached Files */}
          {a.files?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {a.files.map((f, idx) => (
                <a key={idx} href={`${API_URL}/api/assignments/${a.id}/download/${idx}?token=${localStorage.getItem('rasheedgpt_token')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 border border-white/10 hover:border-indigo-500/30 transition-colors text-[10px] text-white/60 hover:text-white/80"
                  data-testid={`principal-file-download-${a.id}-${idx}`}>
                  <Paperclip className="w-3 h-3 text-indigo-400" />
                  {f.filename}
                  <Download className="w-3 h-3 text-indigo-400/50" />
                </a>
              ))}
            </div>
          )}
          <div className="mt-2 text-white/20 text-[9px]">{formatDate(a.created_at)}</div>
        </div>
      ))}
      {data.assignments.length === 0 && <EmptyState text="لم يُنشئ المعلم أي واجب بعد" />}
    </div>
  );
}

// Teacher Notes Tab
function TeacherNotesTab({ data }) {
  if (!data) return <LoadingSpinner />;

  return (
    <div className="space-y-3" data-testid="teacher-notes-tab">
      <p className="text-white/40 text-xs">{data.notes.length} ملاحظة</p>
      {data.notes.map((n, idx) => (
        <div key={idx} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 flex items-start gap-3">
          <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <StickyNote className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/80 text-xs">{n.note}</p>
            <div className="flex items-center gap-2 mt-1">
              {n.student_name && <span className="text-[#4da6ff] text-[10px]">للطالب: {n.student_name}</span>}
              <span className="text-white/20 text-[9px]">{formatDate(n.created_at)}</span>
            </div>
          </div>
        </div>
      ))}
      {data.notes.length === 0 && <EmptyState text="لم يكتب المعلم أي ملاحظة بعد" />}
    </div>
  );
}


// Principal → Teacher Comments Tab
function PrincipalTeacherCommentsTab({ comments, teacherId, headers, onRefresh }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/principal/teacher-comments`, { teacher_id: teacherId, comment: text }, { headers });
      toast.success('تم إرسال الملاحظة');
      setText('');
      onRefresh();
    } catch { toast.error('فشل الإرسال'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="principal-teacher-comments-tab">
      <p className="text-white/40 text-xs">ملاحظات المدير للمعلم (تظهر في لوحة المعلم)</p>
      {/* Send form */}
      <div className="flex gap-2" data-testid="principal-comment-form">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="اكتب ملاحظتك للمعلم..."
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500/50"
          onKeyDown={e => e.key === 'Enter' && handleSend()} data-testid="principal-comment-input" />
        <Button size="sm" onClick={handleSend} disabled={sending} className="bg-indigo-500 hover:bg-indigo-600 shrink-0" data-testid="principal-comment-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
      {/* Comments list */}
      {!comments ? <LoadingSpinner /> : (
        <div className="space-y-2">
          {comments.map(c => (
            <PrincipalCommentCard key={c.id} comment={c} headers={headers} onRefresh={onRefresh} />
          ))}
          {comments.length === 0 && <EmptyState text="لا توجد ملاحظات" small />}
        </div>
      )}
    </div>
  );
}

function PrincipalCommentCard({ comment: c, headers, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.comment);
  const [saving, setSaving] = useState(false);

  const handleEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/principal/teacher-comments/${c.id}`, { comment: editText }, { headers });
      toast.success('تم التعديل');
      setEditing(false);
      onRefresh();
    } catch { toast.error('فشل التعديل'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('هل تريد حذف هذه الملاحظة؟')) return;
    try {
      await axios.delete(`${API_URL}/api/principal/teacher-comments/${c.id}`, { headers });
      toast.success('تم الحذف');
      onRefresh();
    } catch { toast.error('فشل الحذف'); }
  };

  return (
    <div className="p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/10">
      {editing ? (
        <div className="space-y-2">
          <input value={editText} onChange={e => setEditText(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            onKeyDown={e => e.key === 'Enter' && handleEdit()} />
          <div className="flex gap-1.5">
            <Button size="sm" onClick={handleEdit} disabled={saving} className="bg-emerald-500 text-xs">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'حفظ'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditText(c.comment); }} className="text-white/40 text-xs">إلغاء</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-white/80 text-xs">{c.comment}{c.edited_at && <span className="text-white/20 text-[9px] mr-1">(معدّل)</span>}</p>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-white/20 text-[9px]">{formatDate(c.created_at)}</span>
            <div className="flex items-center gap-2">
              {c.read_at ? (
                <span className="flex items-center gap-1 text-[9px] text-emerald-400"><MailOpen className="w-3 h-3" /> قرأها {formatDate(c.read_at)}</span>
              ) : (
                <span className="flex items-center gap-1 text-[9px] text-amber-400"><Mail className="w-3 h-3" /> لم يقرأها بعد</span>
              )}
              <button onClick={() => setEditing(true)} className="text-white/20 hover:text-white/50" data-testid={`edit-comment-${c.id}`}><Pencil className="w-3 h-3" /></button>
              <button onClick={handleDelete} className="text-white/20 hover:text-red-400" data-testid={`delete-comment-${c.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Director Messages View (on Principal Dashboard)
function DirectorMessagesView({ headers }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(() => {
    axios.get(`${API_URL}/api/principal/my-messages`, { headers })
      .then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/principal/my-messages/${id}/read`, {}, { headers });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m));
    } catch { toast.error('فشل التحديث'); }
  };

  if (loading) return <LoadingSpinner />;

  const unread = messages.filter(m => !m.read_at).length;

  return (
    <div className="space-y-4" data-testid="principal-director-messages">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-amber-400 flex items-center gap-2">
          <Mail className="w-5 h-5" /> رسائل مدير المديرية ({messages.length})
        </h2>
        {unread > 0 && (
          <span className="text-[10px] px-3 py-1 rounded-full bg-red-500/20 text-red-400 font-bold">
            {unread} غير مقروءة
          </span>
        )}
      </div>

      <div className="space-y-2">
        {messages.map(m => (
          <Card key={m.id} className={`border-white/10 transition-all ${m.read_at ? 'bg-white/[0.03]' : 'bg-amber-500/5 border-amber-500/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.read_at ? 'bg-white/5' : 'bg-amber-500/20'}`}>
                    {m.read_at ? <MailOpen className="w-4 h-4 text-white/30" /> : <Mail className="w-4 h-4 text-amber-400" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${m.read_at ? 'text-white/60' : 'text-white font-medium'}`}>{m.comment}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/30">
                      <span>من: {m.director_name}</span>
                      <span>{formatDate(m.created_at)}</span>
                    </div>
                  </div>
                </div>
                {!m.read_at && (
                  <Button size="sm" variant="ghost" onClick={() => markAsRead(m.id)}
                    className="text-emerald-400 hover:text-emerald-300 text-[10px] shrink-0" data-testid={`mark-read-${m.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 ml-1" /> تم القراءة
                  </Button>
                )}
                {m.read_at && (
                  <span className="text-emerald-400 text-[9px] flex items-center gap-1 shrink-0">
                    <CheckCircle className="w-3 h-3" /> مقروءة
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {messages.length === 0 && <EmptyState text="لا توجد رسائل من مدير المديرية" />}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
// STUDENTS VIEW
// ─────────────────────────────────────────────
function StudentsView({ headers }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('hi');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/principal/students`, { headers })
      .then(r => setStudents(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <LoadingSpinner />;

  const withGrades = students.filter(s => s.avg_grade != null);
  const avgAll = withGrades.length ? Math.round(withGrades.reduce((a, s) => a + s.avg_grade, 0) / withGrades.length) : 0;
  const lowCount = withGrades.filter(s => s.avg_grade < 50).length;
  const noResCount = students.length - withGrades.length;
  const gradeNums = [...new Set(students.map(s => s.grade_number).filter(Boolean))].sort((a, b) => a - b);

  const sGrade = a => a == null ? ['بلا نتائج', 'var(--mut)'] : a >= 90 ? ['ممتاز', 'var(--easy)'] : a >= 75 ? ['جيد جدًا', 'var(--easy)'] : a >= 50 ? ['مقبول', 'var(--med)'] : ['يحتاج متابعة', 'var(--hard)'];

  let filtered = students.filter(s =>
    (gradeFilter === 'all' || String(s.grade_number) === String(gradeFilter)) &&
    (!search || s.name?.includes(search) || s.username?.includes(search))
  );
  filtered = [...filtered].sort((a, b) => {
    if (sort === 'name') return (a.name || '').localeCompare(b.name || '', 'ar');
    const x = a.avg_grade == null ? -1 : a.avg_grade, y = b.avg_grade == null ? -1 : b.avg_grade;
    return sort === 'lo' ? (a.avg_grade == null ? 1 : b.avg_grade == null ? -1 : x - y) : y - x;
  });

  const s3 = [
    { i: Users, l: 'عدد الطلاب', v: students.length, c: 'var(--pk)' },
    { i: BarChart3, l: 'المعدل العام', v: `${avgAll}%`, c: col(avgAll) },
    { i: AlertTriangle, l: 'يحتاجون متابعة', v: lowCount, c: lowCount ? 'var(--hard)' : 'var(--easy)' },
    { i: Clock, l: 'بلا نتائج', v: noResCount, c: 'var(--mut)' },
  ];

  return (
    <div className="pn" data-testid="principal-students">
      <div className="hd" style={{ '--c': 'var(--pk)' }}>
        <div className="t"><Users /></div>
        <h2>الطلاب ({students.length})</h2>
      </div>
      <div className="s3">
        {s3.map(k => { const I = k.i; return (
          <div className="s3i" key={k.l} style={{ '--c': k.c }}><div className="t"><I /></div><div><b>{k.v}</b><span>{k.l}</span></div></div>
        ); })}
      </div>
      <div className="cd">
        <div className="fl2">
          <label className="sb">
            <Search />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="ابحث عن طالب" data-testid="student-search-input" />
          </label>
          <select className="sel" value={sort} onChange={e => setSort(e.target.value)} aria-label="الترتيب" style={{ width: 'auto', minWidth: 150 }} data-testid="student-sort">
            <option value="hi">الأعلى معدلًا</option>
            <option value="lo">الأدنى معدلًا</option>
            <option value="name">حسب الاسم</option>
          </select>
          <div className="cph">
            <button className="chb" aria-pressed={gradeFilter === 'all'} onClick={() => setGradeFilter('all')} data-testid="grade-filter-all">الكل</button>
            {gradeNums.map(g => (
              <button key={g} className="chb" aria-pressed={String(gradeFilter) === String(g)} onClick={() => setGradeFilter(g)} data-testid={`grade-filter-${g}`}>الصف {g}</button>
            ))}
          </div>
        </div>
        <div className="ls">
          {filtered.map(s => {
            const c = s.avg_grade == null ? 'var(--mut)' : col(s.avg_grade);
            const g = sGrade(s.avg_grade);
            return (
              <div key={s.id} className="stu" style={{ '--c': c, cursor: 'pointer' }}
                onClick={() => setSelectedStudent(s)} data-testid={`student-card-${s.id}`}>
                <UserAvatar user={s} className="av" fallback={s.name?.charAt(0) || 'ط'} />
                <div className="stu-n">
                  <h3>{s.name}</h3>
                  <div className="mt" style={{ marginTop: 6 }}>
                    {s.grade_number && <span className="tg2">الصف {s.grade_number}</span>}
                    <span className="chp" style={{ '--c': g[1] }}>{g[0]}</span>
                  </div>
                </div>
                <div className="stu-p">
                  {s.avg_grade == null
                    ? <span className="nr">لا توجد نتائج بعد</span>
                    : <><PBar value={s.avg_grade} color={c} /><b>{s.avg_grade}%</b></>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="em"><div className="t"><Search /></div><b>لا توجد نتائج</b><span>جرّب بحثًا أو تصنيفًا آخر</span></div>}
        </div>
      </div>
      {selectedStudent && <StudentDetailDialog student={selectedStudent} headers={headers} onClose={() => setSelectedStudent(null)} />}
    </div>
  );
}


// ─────────────────────────────────────────────
// STUDENT DETAIL DIALOG (Comprehensive)
// ─────────────────────────────────────────────
const STUDENT_TABS = [
  { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
  { id: 'grades', label: 'العلامات', icon: FileSpreadsheet },
  { id: 'lessons', label: 'تقدم الدروس', icon: BookOpen },
  { id: 'olympics', label: 'الأولمبياد', icon: Trophy },
  { id: 'feedback', label: 'التغذية الراجعة', icon: MessageSquare },
];

function StudentDetailDialog({ student, headers, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/principal/students/${student.id}/profile`, { headers })
      .then(r => setProfileData(r.data))
      .catch(() => toast.error('فشل تحميل الملف'));
  }, [student.id, headers]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="tdlg max-w-4xl bg-[#1a1a2e] border-white/10 text-white max-h-[90vh] overflow-hidden p-0" dir="rtl">
        <DialogDescription className="sr-only">ملف الطالب الشامل</DialogDescription>
        {/* Header */}
        <div className="bg-gradient-to-l from-[#4da6ff]/10 to-[#1a1a2e] p-5 border-b border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <UserAvatar user={student} className="w-12 h-12 rounded-full bg-[#4da6ff] flex items-center justify-center shrink-0 text-white font-bold text-lg" fallback={<User className="w-6 h-6 text-white" />} />
              <div>
                <span className="text-xl font-bold">{student.name}</span>
                <p className="text-white/40 text-xs">@{student.username} - الصف {student.grade_number || '-'}</p>
              </div>
              {profileData?.stats?.level && (
                <div className="mr-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20">
                  <Award className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-amber-400 text-xs font-bold">{profileData.stats.level.name}</span>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Sub-Tabs */}
        <div className="flex gap-1 px-4 pt-3 overflow-x-auto no-scrollbar">
          {STUDENT_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-[#4da6ff]/20 text-[#4da6ff] border border-[#4da6ff]/30' : 'text-white/40 hover:text-white/60 hover:bg-white/5'
                }`} data-testid={`student-detail-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {!profileData ? <LoadingSpinner /> : (
            <>
              {activeTab === 'overview' && <StudentOverviewTab data={profileData} />}
              {activeTab === 'grades' && <StudentGradesTab data={profileData} />}
              {activeTab === 'lessons' && <StudentLessonsTab data={profileData} />}
              {activeTab === 'olympics' && <StudentOlympicsTab data={profileData} />}
              {activeTab === 'feedback' && <StudentFeedbackTab data={profileData} />}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Student Overview Tab
function StudentOverviewTab({ data }) {
  const stats = data.stats;
  return (
    <div className="space-y-4" data-testid="student-overview-tab">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard value={data.total_lessons_touched} label="دروس" color="text-[#4da6ff]" />
        <StatCard value={data.total_tools_completed} label="أنشطة مكتملة" color="text-emerald-400" />
        <StatCard value={stats?.total_attempts || 0} label="اختبارات" color="text-amber-400" />
        <StatCard value={stats?.active_days || 0} label="أيام نشاط" color="text-purple-400" />
      </div>

      {/* Average Score */}
      {stats?.avg_score_pct > 0 && (
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="flex justify-between mb-1.5">
            <span className="text-white/50 text-xs">المعدل العام</span>
            <span className={`text-sm font-bold ${getScoreColor(stats.avg_score_pct)}`}>{stats.avg_score_pct}%</span>
          </div>
          <ProgressBar value={stats.avg_score_pct} />
          {stats.trend && stats.trend !== 'neutral' && (
            <div className="mt-2 flex items-center gap-1 text-[10px]">
              <TrendingUp className={`w-3 h-3 ${stats.trend === 'improving' ? 'text-emerald-400' : 'text-red-400'}`} />
              <span className={stats.trend === 'improving' ? 'text-emerald-400' : 'text-red-400'}>
                {stats.trend === 'improving' ? 'اتجاه تحسن' : 'اتجاه تراجع'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Subject Performance */}
      {stats?.subject_stats?.length > 0 && (
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <h4 className="text-white/60 text-xs font-bold mb-3 flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" /> أداء المواد
          </h4>
          <div className="space-y-2">
            {stats.subject_stats.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-white/50 text-[11px] w-24 truncate">{s.name}</span>
                <div className="flex-1"><ProgressBar value={s.pct || s.avg_pct || 0} /></div>
                <span className={`text-[11px] font-bold w-10 text-left ${getScoreColor(s.pct || s.avg_pct || 0)}`}>
                  {s.pct || s.avg_pct || 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Level */}
      {stats?.level && (
        <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-bold">{stats.level.name}</span>
            </div>
            <span className="text-white/30 text-[10px]">{stats.level.xp} XP</span>
          </div>
          {stats.level.next_xp && (
            <ProgressBar value={Math.round((stats.level.xp / stats.level.next_xp) * 100)} color="bg-amber-500" />
          )}
        </div>
      )}

      {/* Teacher Interaction */}
      {stats?.teacher_interaction && (
        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <h4 className="text-[#4da6ff] text-xs font-bold mb-2 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> متابعة القراءة</h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-2 rounded-lg bg-white/5">
              <p className="text-lg font-bold text-emerald-400">
                {stats.teacher_interaction.assignments_read}/{stats.teacher_interaction.assignments_total}
              </p>
              <p className="text-[9px] text-white/40">واجبات مقروءة</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-white/5">
              <p className="text-lg font-bold text-emerald-400">
                {stats.teacher_interaction.plans_seen}/{stats.teacher_interaction.plans_total}
              </p>
              <p className="text-[9px] text-white/40">خطط مشاهَدة</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Student Grades Tab (كشف العلامات)
function StudentGradesTab({ data }) {
  const grades = data.teacher_grades || [];

  return (
    <div className="space-y-4" data-testid="student-grades-tab">
      {grades.length > 0 ? (
        <div className="rounded-xl bg-white/[0.03] border border-white/5 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="py-2.5 px-3 text-white/40 text-right font-medium">الامتحان</th>
                <th className="py-2.5 px-3 text-white/40 text-right font-medium">المادة</th>
                <th className="py-2.5 px-3 text-white/40 text-right font-medium">المعلم</th>
                <th className="py-2.5 px-3 text-white/40 text-center font-medium">العلامة</th>
                <th className="py-2.5 px-3 text-white/40 text-center font-medium">النسبة</th>
                <th className="py-2.5 px-3 text-white/40 text-center font-medium">الحالة</th>
              </tr>
            </thead>
            <tbody>
              {grades.map((g, idx) => {
                const pct = g.max_mark > 0 ? Math.round((g.mark / g.max_mark) * 100) : 0;
                const passed = pct >= 50;
                return (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-3 text-white/80 font-medium">{g.exam_title || '-'}</td>
                    <td className="py-2 px-3 text-white/50">{g.subject_name || '-'}</td>
                    <td className="py-2 px-3 text-white/50">{g.teacher_name || '-'}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`font-bold ${getScoreColor(pct)}`}>{g.mark}/{g.max_mark}</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`font-bold ${getScoreColor(pct)}`}>{pct}%</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      {passed
                        ? <span className="text-emerald-400 flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3" /> ناجح</span>
                        : <span className="text-red-400 flex items-center justify-center gap-1"><XCircle className="w-3 h-3" /> راسب</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState text="لا توجد علامات مسجلة لهذا الطالب" />
      )}
    </div>
  );
}

// Student Lessons Progress Tab
function StudentLessonsTab({ data }) {
  const TOOL_LABELS = {
    teaching_guide: 'دليل تدريس',
    worksheet: 'ورقة عمل',
    quiz: 'اختبار',
    terms: 'مصطلحات',
    story: 'قصة',
    explain: 'شرح',
    chat: 'محادثة',
  };

  const lessons = data.lessons || [];

  return (
    <div className="space-y-3" data-testid="student-lessons-tab">
      <p className="text-white/40 text-xs">{lessons.length} درس</p>
      {lessons.map(l => (
        <div key={l.lesson_id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-white text-sm font-medium">{l.title}</p>
              <p className="text-white/30 text-[10px]">{l.subject_name} - {l.grade_name}</p>
            </div>
            <span className="text-emerald-400 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10">
              {l.completed_tools.length} نشاط
            </span>
          </div>
          {/* Completed tools */}
          <div className="flex flex-wrap gap-1 mb-2">
            {l.completed_tools.map((tool, i) => (
              <span key={i} className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400/80">
                {TOOL_LABELS[tool] || tool}
              </span>
            ))}
          </div>
          {/* Scores */}
          {l.scores?.length > 0 && (
            <div className="space-y-1 mt-2 pt-2 border-t border-white/5">
              {l.scores.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[10px]">
                  <span className="text-white/40">{TOOL_LABELS[s.tool_type] || s.tool_type} (محاولة {s.attempt_num})</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${getScoreColor(s.pct)}`}>{s.score}/{s.total} ({s.pct}%)</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {lessons.length === 0 && <EmptyState text="لم يبدأ الطالب أي درس بعد" />}
    </div>
  );
}

// Student Olympics Tab
function StudentOlympicsTab({ data }) {
  const olympics = data.stats?.olympics;

  if (!olympics || !olympics.subjects?.length) {
    return <EmptyState text="لم يشارك الطالب في الأولمبياد بعد" />;
  }

  return (
    <div className="space-y-4" data-testid="student-olympics-tab">
      {/* Summary */}
      <div className="grid grid-cols-4 gap-2">
        <StatCard value={olympics.total_correct} label="صحيحة" color="text-emerald-400" />
        <StatCard value={olympics.total_wrong} label="خاطئة" color="text-red-400" />
        <StatCard value={olympics.total_attempts} label="محاولات" color="text-[#4da6ff]" />
        <StatCard value={`${olympics.total_time}ث`} label="الوقت" color="text-purple-400" />
      </div>

      {/* Per Subject */}
      {olympics.subjects.map((s, idx) => {
        const pct = s.pct || 0;
        return (
          <div key={idx} className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span className="text-white text-sm font-bold">{s.name_ar}</span>
              </div>
              {s.rank > 0 && (
                <span className="text-amber-400 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10">
                  المركز {s.rank} من {s.total_grade_students}
                </span>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-[10px] mb-2">
              <div><span className="text-emerald-400 font-bold">{s.correct}</span><p className="text-white/30">صحيحة</p></div>
              <div><span className="text-red-400 font-bold">{s.wrong}</span><p className="text-white/30">خاطئة</p></div>
              <div><span className="text-[#4da6ff] font-bold">{s.total_questions}</span><p className="text-white/30">سؤال</p></div>
              <div><span className="text-purple-400 font-bold">{s.total_time}ث</span><p className="text-white/30">الوقت</p></div>
            </div>
            <ProgressBar value={pct} color="bg-amber-500" />
            <p className="text-white/30 text-[9px] mt-1 text-left">{pct}% من الأسئلة</p>
          </div>
        );
      })}
    </div>
  );
}

// Student Feedback Tab (AI-generated scores and feedback)
function StudentFeedbackTab({ data }) {
  const TOOL_LABELS = {
    teaching_guide: 'دليل تدريس',
    worksheet: 'ورقة عمل',
    quiz: 'اختبار',
    terms: 'مصطلحات',
    story: 'قصة',
    explain: 'شرح',
    chat: 'محادثة',
  };

  // Collect all scores with feedback
  const allFeedback = [];
  (data.lessons || []).forEach(l => {
    (l.scores || []).forEach(s => {
      if (s.feedback) {
        allFeedback.push({
          lesson: l.title,
          subject: l.subject_name,
          tool: s.tool_type,
          score: s.score,
          total: s.total,
          pct: s.pct,
          feedback: s.feedback,
          date: s.submitted_at,
        });
      }
    });
  });

  // Chat analysis
  const chatAnalysis = data.stats?.chat_analysis;

  return (
    <div className="space-y-4" data-testid="student-feedback-tab">
      {/* Chat Behavior */}
      {chatAnalysis && (
        <div className="p-3 rounded-xl bg-purple-500/5 border border-purple-500/10">
          <h4 className="text-purple-400 text-xs font-bold mb-2 flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" /> تحليل المحادثات
          </h4>
          <div className="grid grid-cols-2 gap-2 text-center mb-3">
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-lg font-bold text-purple-400">{chatAnalysis.total_messages}</p>
              <p className="text-[9px] text-white/30">رسالة</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5">
              <p className="text-lg font-bold text-purple-400">{chatAnalysis.lessons_chatted}</p>
              <p className="text-[9px] text-white/30">درس</p>
            </div>
          </div>
          <div className="space-y-1">
            {chatAnalysis.categories.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-[10px]">
                <span className="text-white/50">{c.name}</span>
                <span className="text-purple-400 font-bold">{c.count} ({c.pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Feedback */}
      {allFeedback.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-white/60 text-xs font-bold flex items-center gap-1">
            <Star className="w-3.5 h-3.5" /> التغذية الراجعة من الأنشطة ({allFeedback.length})
          </h4>
          {allFeedback.map((f, idx) => (
            <div key={idx} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-white text-xs font-medium">{f.lesson}</p>
                  <p className="text-white/30 text-[10px]">{f.subject} - {TOOL_LABELS[f.tool] || f.tool}</p>
                </div>
                <span className={`text-xs font-bold ${getScoreColor(f.pct)}`}>{f.score}/{f.total} ({f.pct}%)</span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed bg-white/[0.02] rounded-lg p-2">{f.feedback}</p>
              <span className="text-white/20 text-[9px] block mt-1">{formatDate(f.date)}</span>
            </div>
          ))}
        </div>
      ) : (
        !chatAnalysis && <EmptyState text="لا توجد تغذية راجعة بعد" />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────
const StatCard = ({ value, label, color }) => (
  <div className="text-center bg-white/[0.03] rounded-xl p-3 border border-white/5">
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-white/30 text-[10px] mt-0.5">{label}</p>
  </div>
);

const ProgressBar = ({ value, color = '' }) => (
  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color || (value >= 80 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500')}`}
      style={{ width: `${Math.min(100, value)}%` }} />
  </div>
);


// Minister Messages View for Principal
function MinisterMsgsView({ headers }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchMsgs = useCallback(() => { axios.get(`${API_URL}/api/principal/minister-messages`, { headers }).then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [headers]);
  useEffect(() => { fetchMsgs(); }, [fetchMsgs]);
  const markRead = async (id) => { try { await axios.post(`${API_URL}/api/principal/minister-messages/${id}/read`, {}, { headers }); setMessages(p => p.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m)); } catch {} };
  if (loading) return <LoadingSpinner />;
  const unread = messages.filter(m => !m.read_at).length;
  return (<div className="space-y-4" data-testid="principal-minister-messages">
    <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2"><Star className="w-5 h-5" /> رسائل الوزير ({messages.length})</h2>{unread > 0 && <span className="text-[10px] px-3 py-1 rounded-full bg-red-500/20 text-red-400 font-bold">{unread} غير مقروءة</span>}</div>
    <div className="space-y-2">{messages.map(m => (<Card key={m.id} className={`border-white/10 ${m.read_at ? 'bg-white/[0.03]' : 'bg-yellow-500/5 border-yellow-500/20'}`}><CardContent className="p-4"><div className="flex items-start justify-between gap-3"><div className="flex items-start gap-3 flex-1"><div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.read_at ? 'bg-white/5' : 'bg-yellow-500/20'}`}>{m.read_at ? <Eye className="w-4 h-4 text-white/30" /> : <Star className="w-4 h-4 text-yellow-400" />}</div><div className="flex-1"><p className={`text-sm ${m.read_at ? 'text-white/60' : 'text-white font-medium'}`}>{m.comment}</p><div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/30"><span>من: {m.minister_name}</span><span>{formatDate(m.created_at)}</span></div></div></div>
      {!m.read_at ? <Button size="sm" variant="ghost" onClick={() => markRead(m.id)} className="text-emerald-400 hover:text-emerald-300 text-[10px] shrink-0"><CheckCircle className="w-3.5 h-3.5 ml-1" /> تم القراءة</Button> : <span className="text-emerald-400 text-[9px] flex items-center gap-1 shrink-0"><CheckCircle className="w-3 h-3" /> مقروءة</span>}
    </div></CardContent></Card>))}</div>
    {messages.length === 0 && <EmptyState text="لا توجد رسائل من الوزير" />}
  </div>);
}

const LoadingSpinner = () => (
  <div className="text-white/30 text-center py-12 text-sm">جارٍ التحميل...</div>
);

const EmptyState = ({ text, small }) => (
  <div className={`text-white/30 text-center ${small ? 'py-4 text-xs' : 'py-8 text-sm'} bg-white/[0.02] rounded-xl`}>{text}</div>
);

function getScoreColor(pct) {
  if (pct >= 80) return 'text-emerald-400';
  if (pct >= 50) return 'text-amber-400';
  return 'text-red-400';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('ar', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
}
